<template>
  <v-card
    :max-width="maxWidth"
    class="pt-3 px-4"
    flat
    :outlined="true"
    :color="cardColor"
  >
    <v-card-title class="mdf-title" style="word-break: break-word">
      <h1 :class="titleStyle">
        <v-icon v-if="dialogStyle === 'Alert'" class="mb-2" large color="red"
          >mdi-alert-outline</v-icon
        >
        {{ title }}
      </h1>
    </v-card-title>
    <v-card-text
      class="mdf-message"
      :class="{ 'mdf-message--subdued': doSubdueMessage }"
    >
      <div class="text-h6 font-weight-medium ml-4">
        {{ message }}
      </div>
    </v-card-text>
    <!-- Content between the message and the buttons, for a dialog that is more
         than a question (PasskeysDialog's table). Empty for every other caller. -->
    <slot />
    <v-card-actions>
      <v-spacer />
      <!-- variant + colour are load-bearing, not decoration. Vuetify 3's
           VCardActions does provideDefaults({ VBtn: { slim, variant: 'text' } }),
           so an unqualified button in here renders TRANSPARENT with no shadow —
           measured rgba(0,0,0,0) against Bridge's #F5F5F5. V2's v-card__actions
           injected nothing. Same fix already carried by EntityMaskForm and
           EntityDeleteForm. -->
      <v-btn
        v-if="!doHideCancelButton"
        ref="btn"
        :disabled="isProcessing"
        :rounded="true"
        variant="elevated"
        :color="cancelButtonColor"
        class="mt-6 mb-5 mr-3 text-high-emphasis"
        :width="cancelButtonWidth"
        @click="cancel"
      >
        {{ cancelButtonLabel }}
      </v-btn>
      <v-btn
        ref="btn"
        :disabled="isProcessing || isOkDisabled"
        :loading="isProcessing"
        :rounded="true"
        variant="elevated"
        :width="calculatedOkButtonWidth"
        class="mt-6 mb-5"
        :color="okButtonColor"
        type="submit.prevent"
        @click="verifyOrDoProcess"
      >
        <v-icon v-if="isVerifyingDoProcess" left> mdi-alert </v-icon>
        {{ okButtonLabel }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { isDarkTheme } from '~/cynqueModules/nuxtAccessors'
/* eslint-disable no-console */

export default {
  props: {
    title: {
      type: String,
      default: 'Title',
    },
    subtitle: {
      type: String,
      default: null,
    },
    message: {
      type: String,
      default: 'Message',
    },
    dialogStyle: {
      type: String,
      default: null,
    },
    doHideCancelButton: {
      type: Boolean,
      default: false,
    },
    okShouldActLikeCancel: {
      type: Boolean,
      default: false,
    },
    okButtonCaptionVerbOverride: {
      type: String,
      default: null,
    },
    cancelButtonCaptionVerbOverride: {
      type: String,
      default: null,
    },
    maxWidth: {
      type: Number,
      required: true,
    },
    okButtonWidth: {
      type: Number,
      default: 150,
    },
    cancelButtonWidth: {
      type: Number,
      default: 150,
    },
    doTintAndDarkenOutlineForLightTheme: {
      type: Boolean,
      default: false,
    },
    doSkipVerification: {
      type: Boolean,
      default: false,
    },
    isShowing: {
      type: Boolean,
      default: false,
    },
    isProcessing: {
      type: Boolean,
      default: false,
    },
    isOkDisabled: {
      type: Boolean,
      default: false,
    },
    doSubdueMessage: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['cancel', 'ok'],
  data: () => ({
    mn: '[components/ModalDialogForm.vue] |',
    isVerifyingDoProcess: false,
  }),
  computed: {
    cardColor() {
      return isDarkTheme() ? '#333' : undefined
    },
    titleStyle() {
      return `text-h5 ${this.titleColor} ${this.titleDarkness}`
    },
    titleColor() {
      // V3: the shade must be part of the color token (V2's separate
      // `text--darken/lighten-N` modifier classes are gone). Alert darkens in light
      // mode; the default title is primary lighten-1 in both modes.
      if (this.dialogStyle === 'Alert') {
        return isDarkTheme() ? 'text-red' : 'text-red-darken-1'
      }
      return 'text-primary-lighten-1'
    },
    titleDarkness() {
      // Now carries only the font-weight half of the old class (shade moved into
      // titleColor above); kept as a separate slot in titleStyle for minimal churn.
      if (this.dialogStyle === 'Alert') {
        return 'font-weight-medium'
      }
      return isDarkTheme() ? '' : 'font-weight-medium'
    },
    calculatedOkButtonWidth() {
      const defaultOkButtonWidth = 150
      // prop okButtonWidth may have been passed in, but may be null
      const okButtonWidthToUse = this.okButtonWidth
        ? this.okButtonWidth
        : defaultOkButtonWidth
      return this.isVerifyingDoProcess
        ? okButtonWidthToUse + 50
        : okButtonWidthToUse
    },
    okButtonCaptionVerb() {
      return this.okButtonCaptionVerbOverride
        ? this.okButtonCaptionVerbOverride
        : 'OK'
    },
    okButtonCaptionVerbUpperCase() {
      return this.okButtonCaptionVerb.toUpperCase()
    },
    cancelButtonLabel() {
      return this.cancelButtonCaptionVerbOverride || 'Cancel'
    },
    okButtonLabel() {
      return this.isVerifyingDoProcess
        ? `Really ${this.okButtonCaptionVerb}?`
        : this.okButtonCaptionVerb
    },
    // Bridge named no colour and took the framework's DEFAULT button fill, which
    // is theme-aware: #F5F5F5 light, #272727 with a white label in dark. Name
    // both so the flattening fix above does not pin the light value into dark.
    cancelButtonColor() {
      return isDarkTheme() ? '#272727' : 'grey-lighten-4'
    },
    okButtonColor() {
      if (isDarkTheme()) {
        return this.isVerifyingDoProcess
          ? 'error'
          : this.dialogStyle === 'Alert'
          ? 'red-darken-4'
          : 'primary-darken-1'
      }
      return this.isVerifyingDoProcess
        ? 'error'
        : this.dialogStyle === 'Alert'
        ? 'red'
        : 'primary'
    },
  },
  watch: {
    isShowing(newValue) {
      if (newValue) {
        this.isVerifyingDoProcess = false
      }
    },
  },
  methods: {
    verifyOrDoProcess() {
      const fn = `${this.mn} verifyOrDoProcess |`
      if (this.okShouldActLikeCancel) {
        this.cancel()
        return
      }
      if (!this.isVerifyingDoProcess) {
        console.debug(
          `${fn} BUTTON PUSHED: ${this.okButtonCaptionVerbUpperCase}`
        )
        if (this.doSkipVerification) {
          console.debug(
            `${fn} doSkipVerification is true. Skipping verification.`
          )
          this.$emit('ok', this.entityData)
          return
        }
        console.debug(`${fn} doSkipVerification is false. Verifying.`)
        this.isVerifyingDoProcess = true
        return
      }
      console.debug(
        `${fn} BUTTON PUSHED: REALLY ${this.okButtonCaptionVerbUpperCase}`
      )
      this.$emit('ok', this.entityData)
    },
    cancel() {
      console.debug(`${this.mn} cancel | BUTTON PUSHED: CANCEL`)
      this.isVerifyingDoProcess = false
      this.$emit('cancel')
    },
  },
}
</script>

<style scoped>
/* Restore Bridge's title/message box padding. Measured on the "Signing is
   forever" alert, Bridge vs nuxt4:

     .v-card-title   padding 16px top / 10px bottom  ->  8px / 8px
     .v-card-text    padding-top 0                   ->  16px

   The card's own pt-3 (12px) is identical in both, so the title's 8px shortfall
   is the whole of the reported top-edge-to-title gap: 12+16=28 on Bridge against
   12+8=20 here. The message's top padding is V2's adjacency rule
   (`.v-card__title + .v-card__text { padding-top: 0 }`), which V3 dropped —
   the same rule already restored for EntityMaskForm's message block.

   Marker classes rather than bare element selectors, so a Vuetify restyle can't
   silently detach these; scoped gives (0,2,0), clearing Vuetify's (0,1,0). */
.v-card-title.mdf-title {
  padding-top: 16px;
  padding-bottom: 10px;
}
/* Doubled class deliberately: Vuetify adds a DIALOG-SPECIFIC rule,
   `.v-dialog > .v-overlay__content > .v-card > .v-card-text { padding: 16px }`
   at (0,4,0), which outranks both a single marker class AND Vuetify's own
   `.v-card-title + .v-card-text { padding-top: 0 }`. Repeating the class takes
   this to (0,4,0)+[data-v], clearing it. Same trick as ContractsCard's subtitle.
   Vuetify 2 had no dialog-scoped padding rule at all, hence Bridge's 0. */
.v-card-text.mdf-message.mdf-message {
  padding-top: 0;
  /* Bridge measures 20px here; V3's dialog rule sets 16px all round and the
     base .v-card-text 1rem, landing on 24px in this dialog. */
  padding-bottom: 20px;
}
/* A subdued message (the `doSubdueMessage` prop): EntityMaskForm's rule of the
   same name, so the two dialog families subdue alike. Regular weight, so the
   title leads, and a step lighter than the default message text: dark 204,
   light grey-800. The weight needs !important to beat `font-weight-medium`. */
.mdf-message--subdued > div {
  font-weight: 400 !important;
}
.v-theme--dark .mdf-message--subdued > div {
  color: rgb(204, 204, 204);
}
.v-theme--light .mdf-message--subdued > div {
  color: rgb(66, 66, 66);
}
.v-divider {
  border-color: rgb(var(--v-theme-primary-lighten-1));
}
.v-theme--dark.v-divider {
  border-color: rgb(var(--v-theme-primary-darken-1));
}
</style>
